import type { Page } from '../../app/feature';
import type { DrawEvent, PanelUser } from '../../shared/types';
import { delegate, esc, inputValue, q, setInputValue } from '../../shared/dom';
import { t } from '../../shared/i18n';
import { dateTime, fmt, money } from '../../shared/format';
import { parseMoney } from '../../shared/ui/money-input';
import { getState } from '../../shared/store/store';
import { eventPrizes, leadById } from '../../shared/store/selectors';
import { hydrateFromApi } from '../../shared/store/sync';
import { ApiError } from '../../shared/api/http';
import { toast } from '../../shared/ui/toast';
import { eligiblePool } from '../../domain/draw-engine';
import { href, navigate } from '../../app/router';
import { eventsApi } from './events.api';
import { prizesApi } from '../prizes/prizes.api';
import { usersApi } from '../users/users.api';

let eventId: string | null = null;
let hosts: PanelUser[] = [];
let editing = false;

/**
 * Página de um sorteio: dados da transmissão, prêmios e ordem da roda, anfitrião,
 * bolão elegível, resultados. Tudo o que a live precisa fica aqui.
 */
export const eventDetail: Page = {
  id: 'sorteo',
  pattern: '/sorteos/:id',
  roles: ['ADMIN', 'SUPPORT'],
  navParent: 'sorteos',

  template: () => `
<div class="crumbs"><a class="auth-link" href="${href('/sorteos')}" data-i18n="ev.back">← Sorteos</a></div>
<div id="ed-missing" class="panel" hidden><div class="empty" data-i18n="ev.notFound">Este sorteo ya no existe.</div></div>
<div id="ed-body">
  <div class="panel hero">
    <div class="hero-main">
      <div class="inline" style="gap:8px; margin-bottom:8px"><span class="chip" id="ed-tier"></span><span class="chip" id="ed-status"></span></div>
      <h2 id="ed-name" class="hero-title">—</h2>
      <p id="ed-meta" class="muted"></p>
    </div>
    <div class="hero-actions">
<span class="chip chip-gold" id="ed-host-note" title=""></span>
<a class="ed-host-card" id="ed-host-card" hidden></a>
      <button class="btn" id="ed-edit" data-i18n="common.edit">Editar</button>
      <span id="ed-close-wrap"><button class="btn btn-ghost" id="ed-close" title="" data-i18n="ev.closeShort">Cerrar sorteo</button></span>
    </div>
    <div class="stats hero-stats">
      <div style="--accent:var(--orange)"><b id="ed-k-prizes">0</b><span id="ed-l-prizes">Premios</span><small id="ed-k-prizes-sub">—</small></div>
      <div style="--accent:var(--gold)"><b id="ed-k-pozo">$0</b><span id="ed-l-pozo">Pozo</span><small id="ed-k-pozo-sub">premios pendientes</small></div>
      <div style="--accent:var(--green)"><b id="ed-k-pool">0</b><span id="ed-l-pool">Cartelas</span><small id="ed-k-pool-sub">—</small></div>
      <div style="--accent:var(--line-2)"><b id="ed-k-draws">0</b><span id="ed-l-draws">Resultados</span><small id="ed-k-draws-sub">premios ya sorteados</small></div>
    </div>
  </div>

  <form class="panel" id="ed-form" hidden style="margin-top:18px" novalidate>
    <div class="panel-head"><h3 data-i18n="ev.editTitle">Datos de la transmisión</h3></div>
    <div class="row">
      <div class="field" style="flex:2 1 220px"><label for="ed-f-name" data-i18n="ev.name">Nombre</label><input type="text" id="ed-f-name"></div>
      <div class="field"><label for="ed-f-tier" data-i18n="ev.type">Tipo</label><select id="ed-f-tier"><option value="SEMANAL" data-i18n="tier.weekly">Semanal</option><option value="MENSUAL" data-i18n="tier.monthly">Mensual</option></select></div>
      <div class="field when-field"><label for="ed-f-date" data-i18n="evn.when">Día y hora</label>
        <div class="when-row"><input type="date" id="ed-f-date" class="when-date"><input type="time" id="ed-f-time" class="when-time" step="300"></div></div>
      <div class="field"><label for="ed-f-status" data-i18n="th.status">Estado</label>
        <select id="ed-f-status"><option value="SCHEDULED" data-i18n="evst.scheduled">Programado</option><option value="LIVE" data-i18n="evst.live">En vivo</option><option value="DONE" data-i18n="evst.done">Realizado</option><option value="CANCELLED" data-i18n="evst.cancelled">Cancelado</option></select></div>
    </div>
    <div class="row">
      <div class="field" style="flex:1 1 100%"><label for="ed-f-link" data-i18n="ev.link">Link de la transmisión (opcional)</label><input type="url" id="ed-f-link" placeholder="https://…"></div>
    </div>
    <div class="inline" style="gap:10px">
      <button type="submit" class="btn btn-primary" data-i18n="common.save">Guardar</button>
      <button type="button" class="btn btn-ghost" id="ed-cancel" data-i18n="common.cancel">Cancelar</button>
      <span class="spacer" style="flex:1"></span>
      <button type="button" class="btn btn-ghost danger" id="ed-delete" data-i18n="ev.delete">Eliminar sorteo</button>
    </div>
  </form>

  <section class="panel ed-winners" id="ed-winners-panel" hidden>
    <div class="panel-head"><h3 id="ed-winners-title">Ganadores</h3><span class="ov-count" id="ed-winners-count"></span></div>
    <ul class="ew-list" id="ed-winners"></ul>
  </section>

  <div class="cols" style="margin-top:18px">
    <div class="panel span-8" id="ed-prizes-panel">
      <div class="panel-head"><h3 data-i18n="ev.prizesTitle">Premios y orden de la rueda</h3></div>
      <p class="hint" data-i18n="ev.prizesHint">La rueda ofrece los premios en este orden. Usa las flechas para reordenar. Un premio ya sorteado no se puede cambiar.</p>
      <form class="row" id="ed-prize-form" novalidate style="margin:10px 0 12px">
        <div class="field" style="flex:0 0 64px"><label for="ed-p-emoji" data-i18n="f.icon">Ícono</label><input type="text" id="ed-p-emoji" value="🎁" maxlength="4"></div>
        <div class="field" style="flex:2 1 160px"><label for="ed-p-name" data-i18n="f.prize">Premio</label><input type="text" id="ed-p-name" placeholder="Bono $50.000 CLP"></div>
        <div class="field" style="flex:1 1 120px"><label for="ed-p-value" data-i18n="f.valueOpt">Valor (CLP) · opcional</label><input type="text" inputmode="numeric" class="money" id="ed-p-value" placeholder="$50.000"></div>
        <div class="field" style="flex:0 0 auto; justify-content:flex-end"><button type="submit" class="btn btn-primary" data-i18n="common.add">Agregar</button></div>
      </form>
      <div class="tw tw-flat">
        <table id="ed-prizes">
          <thead><tr><th style="width:44px">#</th><th data-i18n="th.prize">Premio</th><th data-i18n="th.value">Valor</th><th data-i18n="th.status">Estado</th><th></th></tr></thead>
          <tbody></tbody>
        </table>
      </div>
    </div>

    <div class="stack span-4" id="ed-side">
      <div class="panel">
        <div class="panel-head"><h3 data-i18n="ev.hostTitle">Anfitrión</h3></div>
        <p class="hint" data-i18n="ev.hostHint">Quien conduce la live. Entra con su propio acceso y sólo ve el estudio de este sorteo.</p>
        <p class="ed-host-done" id="ed-host-done" hidden></p>
        <div class="row" style="align-items:end" id="ed-host-row">
          <div class="field" style="flex:1 1 200px"><label for="ed-host" data-i18n="th.host">Anfitrión</label><select id="ed-host"></select></div>
          <div class="field" style="flex:0 0 auto"><a class="btn btn-sm" href="${href('/accesos')}" data-i18n="ev.newHost">Crear acceso</a></div>
        </div>
      </div>
    </div>

    <div class="panel span-12" id="ed-audit-panel">
    <div class="panel-head"><h3 id="ed-audit-title">Resultados</h3><div class="spacer"></div><span class="hint" data-i18n="aud.short">Sello publicado antes, semilla revelada después.</span></div>
    <div class="tw tw-flat">
      <table id="ed-draws">
        <thead><tr><th data-i18n="th.when">Fecha</th><th data-i18n="th.prize">Premio</th><th data-i18n="th.winner">Ganador</th><th data-i18n="th.number">Número</th><th data-i18n="th.pool">Bombo</th><th data-i18n="th.seal">Sello / Semilla</th></tr></thead>
        <tbody></tbody>
      </table>
    </div>
    </div>
  </div>
</div>`,

  mount() {
    q('#ed-edit').onclick = () => { editing = !editing; q('#ed-form').hidden = !editing; if (editing) fillEditForm(); };
    q('#ed-cancel').onclick = () => { editing = false; q('#ed-form').hidden = true; };

    q<HTMLFormElement>('#ed-form').addEventListener('submit', async e => {
      e.preventDefault();
      if (!eventId) return;
      const d = inputValue('#ed-f-date'), h = inputValue('#ed-f-time') || '21:00';
      const when = d ? `${d}T${h}` : '';
      await run(() => eventsApi.update(eventId!, {
        name: inputValue('#ed-f-name'),
        tier: q<HTMLSelectElement>('#ed-f-tier').value as 'SEMANAL' | 'MENSUAL',
        scheduledAt: when ? new Date(when).toISOString() : undefined,
        liveUrl: inputValue('#ed-f-link') || undefined,
        status: q<HTMLSelectElement>('#ed-f-status').value as 'SCHEDULED' | 'LIVE' | 'DONE' | 'CANCELLED',
      }));
      editing = false; q('#ed-form').hidden = true;
    });

    q('#ed-close').onclick = async () => {
      const ev = current(); if (!ev) return;
      if (!confirm(t(ev.tier === 'mensual' ? 'ev.closeConfirmMonthly' : 'ev.closeConfirmWeekly').replace('{name}', ev.name))) return;
      const won = getState().draws.filter(d => d.eventId === ev.id).map(d => d.cartelaId);
      try {
        const r = await eventsApi.close(ev.id, won);
        toast(`${t('msg.eventClosed')} · ${r.consumed} ${t(ev.tier === 'mensual' ? 'ev.closedMonthly' : 'ev.closedWeekly')}`);
        await hydrateFromApi();
      } catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
    };

    q('#ed-delete').onclick = async () => {
      const ev = current(); if (!ev) return;
      if (!confirm(t('ev.confirmDelete').replace('{name}', ev.name))) return;
      try { await eventsApi.remove(ev.id); toast(t('msg.saved')); await hydrateFromApi(); navigate('/sorteos'); }
      catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
    };

    q<HTMLFormElement>('#ed-prize-form').addEventListener('submit', async e => {
      e.preventDefault();
      if (!eventId) return;
      const name = inputValue('#ed-p-name'); if (!name) return;
      await run(() => prizesApi.create(eventId!, { name, value: parseMoney(inputValue('#ed-p-value')), emoji: inputValue('#ed-p-emoji') || undefined }));
      setInputValue('#ed-p-name', ''); setInputValue('#ed-p-value', '');
    });

    const table = q('#ed-prizes');
    delegate(table, 'click', '[data-del]', async el => { await run(() => prizesApi.remove(el.dataset.del as string)); });
    delegate(table, 'click', '[data-move]', async el => {
      if (!eventId) return;
      const ids = eventPrizes(eventId).map(p => p.id);
      const i = ids.indexOf(el.dataset.id as string), j = el.dataset.move === 'up' ? i - 1 : i + 1;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j] as string, ids[i] as string];
      await run(() => prizesApi.reorder(eventId!, ids));
    });

    q<HTMLSelectElement>('#ed-host').onchange = async () => {
      const ev = current(); if (!ev) return;
      const sel = q<HTMLSelectElement>('#ed-host');
      const previous = hosts.find(h => h.eventId === ev.id);
      try {
        if (previous && previous.id !== sel.value) await usersApi.update(previous.id, { eventId: null });
        if (sel.value) await usersApi.update(sel.value, { eventId: ev.id });
        toast(t('msg.saved'));
        await loadHosts(); await hydrateFromApi();
      } catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
    };
  },

  render(params) {
    const id = params.id ?? null;
    if (id !== eventId) { eventId = id; editing = false; q('#ed-form').hidden = true; void loadHosts(); }
    paint();
  }
};

const current = (): DrawEvent | undefined => getState().events.find(e => e.id === eventId);

async function run(action: () => Promise<unknown>): Promise<void> {
  try { await action(); toast(t('msg.saved')); await hydrateFromApi(); }
  catch (err) { toast(err instanceof ApiError ? err.message : t('auth.errGeneric')); }
}

async function loadHosts(): Promise<void> {
  try { hosts = await usersApi.list('HOST'); } catch { hosts = []; }
  paintHosts();
}

function fillEditForm(): void {
  const ev = current(); if (!ev) return;
  setInputValue('#ed-f-name', ev.name);
  q<HTMLSelectElement>('#ed-f-tier').value = ev.tier === 'mensual' ? 'MENSUAL' : 'SEMANAL';
  setInputValue('#ed-f-date', ev.at ? ev.at.slice(0, 10) : '');
  setInputValue('#ed-f-time', ev.at ? ev.at.slice(11, 16) : '21:00');
  setInputValue('#ed-f-link', ev.liveUrl ?? '');
  q<HTMLSelectElement>('#ed-f-status').value = ev.status.toUpperCase();
}

function paintHosts(): void {
  const ev = current();
  const sel = q<HTMLSelectElement>('#ed-host');
  const assigned = ev ? hosts.find(h => h.eventId === ev.id) : undefined;
  sel.innerHTML = `<option value="">${esc(t('ev.nohost'))}</option>` +
    hosts.map(h => `<option value="${h.id}" ${assigned?.id === h.id ? 'selected' : ''}>${esc(h.name)}${h.eventId && h.eventId !== ev?.id ? ` (${esc(t('ev.busy'))})` : ''}</option>`).join('');
}

function paint(): void {
  const s = getState();
  const ev = current();
  q('#ed-missing').hidden = !!ev || !s.events.length;
  q('#ed-body').hidden = !ev;
  if (!ev) return;

  const pz = eventPrizes(ev.id), pend = pz.filter(p => p.status === 'pending');
  const pool = eligiblePool(s, ev);
  const draws = s.draws.filter(d => d.eventId === ev.id);

  q('#ed-tier').textContent = ev.tier === 'mensual' ? t('tier.monthly') : t('tier.weekly');
  q('#ed-tier').className = `chip ${ev.tier === 'mensual' ? 'chip-gold' : 'chip-orange'}`;
  q('#ed-status').textContent = t('evst.' + ev.status);
  q('#ed-status').className = `chip ${ev.status === 'live' ? 'chip-green' : ev.status === 'scheduled' ? 'chip-dim' : 'chip-dim'}`;
  q('#ed-name').textContent = ev.name;
  q('#ed-meta').innerHTML = `📅 ${esc(ev.at || t('ov.noDate'))} · 🎙 ${ev.hostName ? esc(ev.hostName) : esc(t('ev.nohost'))}${ev.liveUrl ? ` · <a class="auth-link" href="${esc(ev.liveUrl)}" target="_blank" rel="noopener">${esc(t('ev.linkShort'))}</a>` : ''}`;
  // o sorteio é conduzido pelo anfitrião no estúdio dele; o painel só acompanha
  const finished = ev.status === 'done' || ev.status === 'cancelled';
  q('#ed-host-note').textContent = ev.hostName ? `🎙 ${t(finished ? 'ev.hostRan' : 'ev.hostRuns')} ${ev.hostName}` : t(finished ? 'ev.nohost' : 'ev.noHostYet');
  // quem conduziu: cartão com avatar, nome e canal (no sorteio realizado substitui o selo)
  const card = q<HTMLAnchorElement>('#ed-host-card');
  card.hidden = !finished || !ev.hostName;
  q('#ed-host-note').hidden = finished && !!ev.hostName;
  if (finished && ev.hostName) {
    const initials = ev.hostName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
    card.href = ev.hostId ? href('/influencers/' + ev.hostId) : href('/influencers');
    card.innerHTML = `<span class="ehc-avatar">${esc(initials)}</span>
      <span class="ehc-body"><small>🎙 ${esc(t('ev.hostRanCard'))}</small><b>${esc(ev.hostName)}</b>${ev.hostHandle ? `<span>${esc(ev.hostHandle)}</span>` : ''}</span>
      <span class="ehc-go" title="${esc(t('ev.hostProfile'))}">→</span>`;
  }

  // sorteio realizado/cancelado: prêmios viram histórico (sem adicionar, reordenar ou excluir)
  const over = ev.status === 'done' || ev.status === 'cancelled';
  q('#ed-prize-form').hidden = over;
  // só consulta: sem editar dados, sem trocar anfitrião
  q('#ed-edit').hidden = over;
  if (over) { editing = false; q('#ed-form').hidden = true; }
  q('#ed-host-row').hidden = over;
  q('[data-i18n="ev.hostHint"]').hidden = over;
  q('#ed-host-done').hidden = !over;
  q('#ed-host-done').textContent = ev.hostName ? `🎙 ${t('ev.hostRan')} ${ev.hostName}` : t('ev.nohost');
  q('#ed-status').className = `chip ${ev.status === 'done' ? 'chip-done' : ev.status === 'live' ? 'chip-green' : 'chip-dim'}`;
  if (ev.status === 'done') q('#ed-status').textContent = '✓ ' + t('evst.done');
  q('[data-i18n="ev.prizesHint"]').textContent = t(over ? 'ev.prizesLocked' : 'ev.prizesHint');

  if (over) {
    // realizado: o que foi entregue e quem participou (retrato do fechamento)
    const delivered = draws.reduce((a, d) => a + (pz.find(p => p.id === d.prizeId)?.value || 0), 0);
    setStat('prizes', `${draws.length}/${pz.length}`, t('host.delivered'), t('ev.statDelivered'));
    setStat('pozo', money(delivered), t('ev.deliveredPozo'), t('ev.statDeliveredSub'));
    setStat('pool', fmt(ev.poolCartelas ?? draws[0]?.poolSize ?? 0), t('stage.cartelas'), t('host.participated'));
    setStat('draws', ev.poolPlayers != null ? fmt(ev.poolPlayers) : '—', t('ov.people'), t('ev.statPeopleSub'));
  } else {
    setStat('prizes', String(pend.length), t('host.prizes'), `${pz.length} ${t('ev.total')}`);
    setStat('pozo', money(pend.reduce((a, p) => a + p.value, 0)), t('stage.pozo'), t('ev.pozoHint'));
    setStat('pool', fmt(pool.length), t('stage.cartelas'), `${fmt(new Set(pool.map(c => c.leadId)).size)} ${t('stage.players').toLowerCase()}`);
    setStat('draws', String(draws.length), t('ev.results'), t('ev.drawsHint'));
  }

  // ganhadores em destaque (um cartão por prêmio; os não sorteados aparecem apagados)
  const wins = pz.filter(p => p.status !== 'cancelled');
  q('#ed-winners-panel').hidden = draws.length === 0;
  q('#ed-winners-title').textContent = t('ev.winnersTitle');
  q('#ed-winners-count').textContent = draws.length ? `${draws.length}/${wins.length}` : '';
  q('#ed-winners').innerHTML = wins.map((p, i) => {
    const d = draws.find(x => x.prizeId === p.id);
    const prize = `<span class="ew-prize"><i>${esc(p.emoji || '🎁')}</i><span>${esc(p.name)}</span>${p.value > 0 ? `<em>${money(p.value)}</em>` : ''}</span>`;
    if (!d) return over ? `<li class="ew ew-miss">
      <span class="ew-pos"><b>${i + 1}º</b></span>
      <span class="ew-body"><span class="ew-kicker">${esc(t('stage.prizeN'))} ${i + 1}</span>${prize}</span>
      <span class="ew-stub"><small>${esc(t('ev.notDrawn'))}</small><span class="ew-dash">— — — — —</span></span>
    </li>` : '';
    const l = leadById(d.leadId);
    return `<li class="ew${i === 0 ? ' ew-first' : ''}">
      <span class="ew-pos"><b>${i + 1}º</b><small>🏆</small></span>
      <span class="ew-body">
        <span class="ew-kicker">${esc(t('st.winner'))} · ${esc(t('stage.prizeN').toLowerCase())} ${i + 1}</span>
        <b class="ew-name">${esc(l?.name ?? '—')}</b>
        ${prize}
        <span class="ew-meta">${esc(dateTime(d.at))}${l?.externalId ? ` · ID ${esc(l.externalId)}` : ''}</span>
      </span>
      <span class="ew-stub"><small>${esc(t('ev.luckyNumber'))}</small><span class="ew-num">${d.number.split('').map(ch => `<b>${ch}</b>`).join('')}</span></span>
    </li>`;
  }).join('');

  // modo consulta: sem painel de prêmios (os ganhadores já mostram tudo); auditoria ao lado do anfitrião
  q('#ed-prizes-panel').hidden = over;
  q('#ed-side').hidden = over; // o anfitrião já aparece no topo
  q('#ed-audit-panel').className = 'panel span-12';
  q('#ed-audit-title').textContent = t(over ? 'ev.auditTitle' : 'ev.results');
  q('#ed-close').title = t(ev.tier === 'mensual' ? 'ev.ruleMonthlyText' : 'ev.ruleWeeklyText');
  q('#ed-close-wrap').hidden = ev.status === 'done' || ev.status === 'cancelled';

  q('#ed-prizes tbody').innerHTML = pz.map((p, i) => {
    const d = draws.find(x => x.prizeId === p.id);
    const l = d ? leadById(d.leadId) : undefined;
    const pending = p.status === 'pending';
    return `<tr class="${pending ? '' : 'muted'}">
      <td class="tnum muted">${String(i + 1).padStart(2, '0')}</td>
      <td><span style="font-size:17px">${p.emoji}</span> <b>${esc(p.name)}</b>${l && d ? `<div class="muted" style="font-size:11.5px">🏆 ${esc(l.name)} · <span class="t-num">${d.number}</span></div>` : ''}</td>
      <td class="t-num">${p.value > 0 ? money(p.value) : `<span class="muted">${esc(t('ev.noValue'))}</span>`}</td>
      <td><span class="chip ${p.status === 'drawn' ? 'chip-green' : pending ? 'chip-orange' : 'chip-dim'}">${p.status === 'drawn' ? t('st.drawn') : pending ? t('st.pending') : t('st.cancelled')}</span></td>
      <td style="text-align:right; white-space:nowrap">
        ${pending && !over ? `<button class="btn btn-sm btn-ghost" data-move="up" data-id="${p.id}" ${i === 0 ? 'disabled' : ''}>↑</button><button class="btn btn-sm btn-ghost" data-move="down" data-id="${p.id}" ${i === pz.length - 1 ? 'disabled' : ''}>↓</button><button class="btn btn-sm btn-ghost" data-del="${p.id}">✕</button>` : ''}
      </td>
    </tr>`;
  }).join('') || `<tr><td colspan="5" class="empty">${esc(t('ev.noPrizesYet'))}</td></tr>`;

  q('#ed-draws tbody').innerHTML = draws.slice().reverse().map(d => {
    const l = leadById(d.leadId), p = pz.find(x => x.id === d.prizeId);
    return `<tr>
      <td class="muted tnum">${dateTime(d.at)}</td>
      <td>${p ? p.emoji + ' ' + esc(p.name) : '—'}</td>
      <td>${l ? esc(l.name) : '—'}</td>
      <td class="t-num">${d.number}</td>
      <td class="tnum muted">${fmt(d.poolSize)}</td>
      <td style="font-family:var(--ff-num); font-size:11px; color:var(--txt-3); line-height:1.5"><div>🔒 ${d.hash.slice(0, 24)}…</div><div>🔑 ${d.seed}</div></td>
    </tr>`;
  }).join('') || `<tr><td colspan="6" class="empty">${esc(t('ev.noDraws'))}</td></tr>`;

  paintHosts();
}

/** Uma estatística do topo: valor, rótulo e legenda (mudam entre sorteio aberto e realizado). */
function setStat(key: 'prizes' | 'pozo' | 'pool' | 'draws', value: string, label: string, sub: string): void {
  q(`#ed-k-${key}`).textContent = value;
  q(`#ed-l-${key}`).textContent = label;
  q(`#ed-k-${key}-sub`).textContent = sub;
}
